import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Film, Tv, Upload, Image, Plus, Trash2, ChevronRight, ChevronLeft,
  CheckCircle, AlertCircle, X, Loader2, Play, Info, ArrowLeft, Calendar, Edit
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { companyApi, legalApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const GENRE_OPTIONS = [
  'Action', 'Adventure', 'Animation', 'Biography', 'Comedy', 'Crime',
  'Documentary', 'Drama', 'Family', 'Fantasy', 'Historical', 'Horror',
  'Musical', 'Mystery', 'Romance', 'Sci-Fi', 'Sports', 'Thriller',
  'War', 'Western',
];

const CONTENT_TAG_CATEGORIES = [
  {
    key: 'violence', label: 'Violence',
    options: ['None', 'Mild fantasy action (no visible blood)', 'Realistic fights or limited blood', 'Intense violence'],
    levels: [0, 1, 2, 3],
  },
  {
    key: 'sexual', label: 'Sexual Content / Nudity',
    options: ['None', 'Mild suggestive themes', 'Non-explicit nudity'],
    levels: [0, 2, 3],
    helper: 'Explicit sexual content is not permitted on this platform.',
  },
  {
    key: 'language', label: 'Language',
    options: ['Clean language', 'Mild profanity', 'Strong language', 'Extreme or constant profanity'],
    levels: [0, 1, 2, 3],
  },
  {
    key: 'horror', label: 'Horror / Fear',
    options: ['None', 'Mild suspense', 'Intense scenes', 'Disturbing horror themes'],
    levels: [0, 1, 2, 3],
  },
];

const calcContentRating = (tags) => {
  if (!tags || tags.violence === null || tags.sexual === null || tags.language === null || tags.horror === null) return null;
  const getLevel = (key, idx) => {
    const cat = CONTENT_TAG_CATEGORIES.find(c => c.key === key);
    return cat?.levels?.[idx] ?? idx;
  };
  const maxLevel = Math.max(
    getLevel('violence', tags.violence),
    getLevel('sexual', tags.sexual),
    getLevel('language', tags.language),
    getLevel('horror', tags.horror),
  );
  if (maxLevel >= 3) return '16+';
  if (maxLevel === 2) return '14+';
  if (maxLevel === 1) return '12+';
  return '7+';
};

const STEPS_FILM = ['Content Type', 'Details', 'Video & Thumbnail', 'Genres & Classification', 'Review & Submit'];
const STEPS_SERIES = ['Content Type', 'Details', 'Thumbnail', 'Genres & Classification', 'Episodes', 'Review & Submit'];

const UploadContentPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const thumbnailInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const epVideoInputRef = useRef(null);
  const epThumbnailInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoStep, setVideoStep] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Draft state
  const [draftId, setDraftId] = useState(null);
  const draftSaveTimeout = useRef(null);
  const isLoadingDraft = useRef(false);

  // Agreement state
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementHtml, setAgreementHtml] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  // Form state
  const [contentType, setContentType] = useState('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    video_file: '',
    videoFileName: '',
    thumbnail: '',
    thumbnailPreview: '',
    genres: [],
    release_date: '',
    release_time: '',
    content_tags: { violence: null, sexual: null, language: null, horror: null },
  });

  // Series episodes state
  const [seasons, setSeasons] = useState([{ season_number: 1, episodes: [] }]);
  const [epForm, setEpForm] = useState({
    title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: ''
  });
  const [activeSeason, setActiveSeason] = useState(0);
  const [uploadingEpThumb, setUploadingEpThumb] = useState(false);
  const [uploadingEpVideo, setUploadingEpVideo] = useState(false);
  const [epVideoProgress, setEpVideoProgress] = useState(0);
  const [epVideoStep, setEpVideoStep] = useState(null);
  const [editingEpIndex, setEditingEpIndex] = useState(null);

  // Auto-save draft (debounced)
  const saveDraft = useCallback(async (overrides = {}) => {
    if (isLoadingDraft.current) return;
    const ct = overrides.contentType ?? contentType;
    const s = overrides.step ?? step;
    const f = overrides.form ?? form;
    const ss = overrides.seasons ?? seasons;
    if (!ct) return; // Don't save until content type is selected
    try {
      const res = await companyApi.saveDraft({
        draft_id: draftId || undefined,
        content_type: ct,
        step: s,
        form: { ...f, thumbnailPreview: undefined }, // Don't save blob URLs
        seasons: ss,
        upload_id: localStorage.getItem('reframes_pending_upload') ? JSON.parse(localStorage.getItem('reframes_pending_upload')).upload_id : undefined,
      });
      if (res.draft_id && !draftId) setDraftId(res.draft_id);
    } catch (e) {
      console.warn('[Draft] Auto-save failed:', e.message);
    }
  }, [contentType, step, form, seasons, draftId]);

  const debouncedSaveDraft = useCallback((overrides = {}) => {
    if (draftSaveTimeout.current) clearTimeout(draftSaveTimeout.current);
    draftSaveTimeout.current = setTimeout(() => saveDraft(overrides), 2000);
  }, [saveDraft]);

  // Auto-save on step change
  useEffect(() => {
    if (contentType && step > 0) saveDraft();
  }, [step]);

  // Auto-save on form change (debounced)
  useEffect(() => {
    if (contentType) debouncedSaveDraft();
    return () => { if (draftSaveTimeout.current) clearTimeout(draftSaveTimeout.current); };
  }, [form.title, form.description, form.video_file, form.thumbnail, form.genres.length, form.release_date, form.content_tags, seasons]);

  // Navigation blocking during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploadingVideo || uploadingEpVideo) {
        e.preventDefault();
        e.returnValue = 'Upload in progress — leaving this page will interrupt the upload. Are you sure?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploadingVideo, uploadingEpVideo]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    const load = async () => {
      try {
        const data = await companyApi.getMyCompany();
        if (!data.company || data.company.status !== 'approved') {
          toast.error('You must be an approved creator to upload content');
          navigate('/company/dashboard');
          return;
        }
        setCompany(data.company);

        // Load draft if URL has ?draft=xxx
        const draftParam = searchParams.get('draft');
        if (draftParam) {
          isLoadingDraft.current = true;
          try {
            const draft = await companyApi.getDraft(draftParam);
            setDraftId(draft.draft_id);
            setContentType(draft.content_type || '');
            setStep(draft.step || 0);
            if (draft.form) {
              setForm(prev => ({ ...prev, ...draft.form, thumbnailPreview: draft.form.thumbnail || '' }));
            }
            if (draft.seasons && draft.seasons.length > 0) {
              setSeasons(draft.seasons);
            }
            toast.success('Draft loaded');
          } catch {
            toast.error('Draft not found');
          } finally {
            isLoadingDraft.current = false;
          }
        }
      } catch { navigate('/company/dashboard'); }
      finally { setLoading(false); }
    };
    load();
  }, [isAuthenticated, navigate, searchParams]);

  const steps = contentType === 'series' ? STEPS_SERIES : STEPS_FILM;
  const totalSteps = steps.length;

  const updateForm = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleGenre = (genre) => {
    setForm(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : prev.genres.length < 3 ? [...prev.genres, genre] : prev.genres,
    }));
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const preview = URL.createObjectURL(file);
      updateForm('thumbnailPreview', preview);
      const result = await companyApi.uploadThumbnail(file);
      updateForm('thumbnail', result.url);
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error(err.message || 'Thumbnail upload failed');
      updateForm('thumbnailPreview', '');
    } finally { setUploadingThumb(false); }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    setVideoProgress(0);
    setVideoStep(null);
    try {
      const result = await companyApi.uploadVideo(file, (pct, step) => {
        setVideoProgress(pct);
        if (step) setVideoStep(step);
      });
      if (result.url) {
        updateForm('video_file', result.url);
        updateForm('videoFileName', file.name);
        if (result.duration) updateForm('duration', result.duration);
        toast.success('Video uploaded successfully');
      } else {
        toast.error('Upload completed but video URL is missing. Please try again.');
      }
    } catch (err) {
      toast.error(
        'Upload interrupted. Your draft has been saved — you can resume it from your Dashboard by re-selecting the same file.',
        { duration: 8000 }
      );
    } finally { setUploadingVideo(false); setVideoProgress(0); setVideoStep(null); }
  };

  const handleEpThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEpThumb(true);
    try {
      const preview = URL.createObjectURL(file);
      setEpForm(p => ({ ...p, thumbnailPreview: preview }));
      const result = await companyApi.uploadThumbnail(file);
      setEpForm(p => ({ ...p, thumbnail: result.url }));
      toast.success('Episode thumbnail uploaded');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingEpThumb(false); }
  };

  const handleEpVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEpVideo(true);
    setEpVideoProgress(0);
    setEpVideoStep(null);
    try {
      const result = await companyApi.uploadVideo(file, (pct, step) => {
        setEpVideoProgress(pct);
        if (step) setEpVideoStep(step);
      });
      if (result.url) {
        setEpForm(p => ({ ...p, video_file: result.url, videoFileName: file.name, duration: result.duration || 0 }));
        toast.success('Episode video uploaded');
      } else {
        toast.error('Upload completed but video URL is missing. Please try again.');
      }
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingEpVideo(false); setEpVideoProgress(0); setEpVideoStep(null); }
  };

  const addSeason = () => {
    setSeasons(prev => [...prev, { season_number: prev.length + 1, episodes: [] }]);
    setActiveSeason(seasons.length);
  };

  const removeSeason = (idx) => {
    if (seasons.length <= 1) return;
    setSeasons(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, season_number: i + 1 })));
    setActiveSeason(Math.max(0, activeSeason - 1));
  };

  const addEpisode = () => {
    if (!epForm.title) { toast.error('Episode title is required'); return; }
    if (!epForm.video_file) { toast.error('Upload a video file for the episode'); return; }
    const season = seasons[activeSeason];
    const epNumber = season.episodes.length + 1;
    const newEp = {
      season_number: season.season_number,
      episode_number: epNumber,
      title: epForm.title,
      description: epForm.description,
      video_file: epForm.video_file,
      thumbnail: epForm.thumbnail,
      release_date: epForm.release_date || undefined,
    };
    setSeasons(prev => prev.map((s, i) =>
      i === activeSeason ? { ...s, episodes: [...s.episodes, newEp] } : s
    ));
    setEpForm({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
    toast.success(`Episode ${epNumber} added to Season ${season.season_number}`);
  };

  const removeEpisode = (seasonIdx, epIdx) => {
    setSeasons(prev => prev.map((s, i) =>
      i === seasonIdx
        ? { ...s, episodes: s.episodes.filter((_, j) => j !== epIdx).map((ep, j) => ({ ...ep, episode_number: j + 1 })) }
        : s
    ));
    if (editingEpIndex === epIdx) { setEditingEpIndex(null); }
  };

  const startEditEpisode = (epIdx) => {
    const ep = seasons[activeSeason].episodes[epIdx];
    setEpForm({
      title: ep.title || '',
      description: ep.description || '',
      video_file: ep.video_file || '',
      videoFileName: ep.videoFileName || ep.video_file?.split('/').pop() || 'video',
      thumbnail: ep.thumbnail || '',
      thumbnailPreview: ep.thumbnail || '',
      release_date: ep.release_date || '',
    });
    setEditingEpIndex(epIdx);
  };

  const saveEditEpisode = () => {
    if (!epForm.title) { toast.error('Episode title is required'); return; }
    if (!epForm.video_file) { toast.error('Upload a video file for the episode'); return; }
    const season = seasons[activeSeason];
    setSeasons(prev => prev.map((s, i) =>
      i === activeSeason
        ? {
            ...s,
            episodes: s.episodes.map((ep, j) =>
              j === editingEpIndex
                ? { ...ep, title: epForm.title, description: epForm.description, video_file: epForm.video_file, videoFileName: epForm.videoFileName, thumbnail: epForm.thumbnail, release_date: epForm.release_date || undefined }
                : ep
            ),
          }
        : s
    ));
    setEditingEpIndex(null);
    setEpForm({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' });
    toast.success(`Episode updated`);
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!contentType;
      case 1: return form.title.trim().length > 0;
      case 2:
        if (contentType === 'series') return !!form.thumbnail; // thumbnail mandatory for series
        return !!form.video_file && !!form.thumbnail; // video AND thumbnail required for films
      case 3: {
        const tagsComplete = form.content_tags.violence !== null && form.content_tags.sexual !== null && form.content_tags.language !== null && form.content_tags.horror !== null;
        return form.genres.length >= 1 && tagsComplete;
      }
      case 4:
        if (contentType === 'series') return seasons.some(s => s.episodes.length > 0);
        return true;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    // Check agreement first
    if (!agreementChecked) {
      try {
        const status = await companyApi.getAgreementStatus();
        setAgreementChecked(true);
        if (status.accepted) {
          setAgreementAccepted(true);
        }
      } catch { /* proceed if endpoint fails */ }
    }

    if (!agreementAccepted) {
      try {
        const page = await legalApi.getPage('content-creator-agreement');
        setAgreementHtml(page.content || '');
      } catch {
        setAgreementHtml('<p>Unable to load agreement. Please try again.</p>');
      }
      setShowAgreement(true);
      return;
    }

    await doSubmitContent();
  };

  const handleAgreementAccept = async () => {
    try {
      await companyApi.acceptAgreement();
      setAgreementAccepted(true);
      setShowAgreement(false);
      await doSubmitContent();
    } catch (err) {
      toast.error(err.message || 'Failed to accept agreement');
    }
  };

  const handleAgreementDecline = () => {
    setShowAgreement(false);
    toast.error('You must accept the Creator Content Submission Agreement to publish content.');
  };

  const doSubmitContent = async () => {
    // Mandatory thumbnail check
    if (!form.thumbnail) {
      toast.error('A thumbnail is required for all content types');
      return;
    }
    setSubmitting(true);
    try {
      const allEpisodes = contentType === 'series'
        ? seasons.flatMap(s => s.episodes)
        : undefined;

      // Combine release_date and release_time into a single ISO string (UTC)
      let releaseDateTime = form.release_date || undefined;
      if (form.release_date && form.release_time) {
        releaseDateTime = `${form.release_date}T${form.release_time}:00Z`;
      }

      await companyApi.submitContent({
        content_type: contentType,
        title: form.title,
        description: form.description,
        video_file: form.video_file || undefined,
        thumbnail: form.thumbnail || '',
        duration: 0,
        genres: form.genres,
        categories: [],
        episodes: allEpisodes,
        release_date: releaseDateTime,
        content_tags: form.content_tags,
        content_rating: calcContentRating(form.content_tags) || '16+',
      });
      toast.success('Content submitted for review!');
      // Delete draft record only (keep B2 files — they belong to the published content now)
      if (draftId) {
        try { await companyApi.deleteDraft(draftId, false); } catch {}
      }
      localStorage.removeItem('reframes_pending_upload');
      navigate('/company/dashboard');
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#030014] flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="upload-content-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/company/dashboard')} className="text-gray-400 hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Upload New Content
        </h1>
        <p className="text-gray-400 mb-8">Submit your AI-generated content for review</p>

        {/* Step Progress */}
        <div className="flex items-center gap-1 mb-10">
          {steps.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? 'text-white' : 'text-gray-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                  i < step ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' :
                  i === step ? 'border-[#8B5CF6] text-[#8B5CF6]' :
                  'border-gray-700 text-gray-600'
                }`}>
                  {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                </div>
                <span className="hidden md:inline text-xs font-medium">{label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-[#8B5CF6]' : 'bg-gray-800'}`} />}
            </div>
          ))}
        </div>

        {/* Step 0: Content Type */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-content-type">
            <h2 className="text-xl font-semibold">What are you uploading?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <button onClick={() => setContentType('film')} data-testid="select-film"
                className={`p-8 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  contentType === 'film' ? 'border-[#8B5CF6] bg-[#8B5CF6]/10' : 'border-gray-800 bg-[#0F0B1E] hover:border-gray-600'
                }`}>
                <Film className={`w-10 h-10 mb-4 ${contentType === 'film' ? 'text-[#8B5CF6]' : 'text-gray-500'}`} />
                <h3 className="text-lg font-bold mb-1">Movie</h3>
                <p className="text-sm text-gray-400">A single video — short movie, feature, documentary</p>
              </button>
              <button onClick={() => setContentType('series')} data-testid="select-series"
                className={`p-8 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  contentType === 'series' ? 'border-[#00FF9D] bg-[#00FF9D]/10' : 'border-gray-800 bg-[#0F0B1E] hover:border-gray-600'
                }`}>
                <Tv className={`w-10 h-10 mb-4 ${contentType === 'series' ? 'text-[#00FF9D]' : 'text-gray-500'}`} />
                <h3 className="text-lg font-bold mb-1">Series</h3>
                <p className="text-sm text-gray-400">Multiple episodes organized by seasons</p>
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Details (no Release Year) */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-details">
            <h2 className="text-xl font-semibold">{contentType === 'series' ? 'Series' : 'Movie'} Details</h2>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={(e) => updateForm('title', e.target.value)}
                placeholder={contentType === 'series' ? 'Enter series title' : 'Enter movie title'}
                className="bg-[#030014] border-[#8B5CF6]/20 h-12 text-lg" data-testid="input-title" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Description <span className={form.description.length >= 500 ? 'text-red-400' : 'text-gray-600'}>({form.description.length}/500)</span></label>
              <Textarea value={form.description} onChange={(e) => updateForm('description', e.target.value.slice(0, 500))}
                placeholder="Describe your content..." className={`bg-[#030014] min-h-[120px] ${form.description.length >= 500 ? 'border-red-500/50' : 'border-[#8B5CF6]/20'}`} data-testid="input-description" />
              {form.description.length >= 500 && <p className="text-xs text-red-400 mt-1">Max 500 characters reached</p>}
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {contentType === 'series' ? 'Series Release Date' : 'Release Date'}
                <span className="text-gray-600 text-xs">(optional)</span>
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="date"
                  value={form.release_date}
                  onChange={(e) => updateForm('release_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-[#030014] border-[#8B5CF6]/20 h-10 max-w-[180px]"
                  data-testid="input-release-date"
                />
                <Input
                  type="time"
                  value={form.release_time}
                  onChange={(e) => updateForm('release_time', e.target.value)}
                  className="bg-[#030014] border-[#8B5CF6]/20 h-10 max-w-[140px]"
                  data-testid="input-release-time"
                />
                <span className="text-xs text-gray-500 whitespace-nowrap">Timezone UTC +0</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {contentType === 'series'
                  ? 'Set a release date for the entire series. You can also set individual episode dates in the episodes step.'
                  : 'Schedule when this movie should be released. Leave empty to publish immediately after approval.'}
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Video & Thumbnail (film) or Thumbnail only (series) */}
        {step === 2 && (
          <div className="space-y-8" data-testid="step-video-thumbnail">
            <h2 className="text-xl font-semibold">
              {contentType === 'series' ? 'Series Thumbnail' : 'Video & Thumbnail'}
            </h2>

            {/* Video Upload — Films only */}
            {contentType !== 'series' && (
              <div className="admin-card p-6 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Play className="w-5 h-5 text-[#8B5CF6]" /> Video File *
                </h3>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
                  <Info className="w-4 h-4 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-gray-300">
                    <p>Supported formats: <strong>MP4, WebM, MOV, AVI, MKV</strong></p>
                    <p>Max file size: 50GB</p>
                    <p className="mt-1">Recommended ratio: <strong>16:9</strong></p>
                  </div>
                </div>
                {form.video_file ? (
                  <div className="flex items-center gap-3 p-3 bg-[#00FF9D]/5 border border-[#00FF9D]/20 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-[#00FF9D]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#00FF9D]">Video uploaded</p>
                      <p className="text-xs text-gray-500 truncate">{form.videoFileName}</p>
                    </div>
                    <button onClick={() => { updateForm('video_file', ''); updateForm('videoFileName', ''); }}
                      className="p-1.5 bg-black/40 rounded-full hover:bg-black/60 transition-colors">
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : uploadingVideo ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-[#8B5CF6] animate-spin" />
                      <span className="text-sm">
                        {videoStep === 'resuming'
                          ? 'Resuming upload...'
                          : videoStep === 'finalizing'
                            ? 'Finalizing...'
                            : `Uploading... ${videoProgress}%`}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-[#8B5CF6] transition-all rounded-full" style={{ width: `${videoProgress}%` }} />
                    </div>
                    {videoStep === 'connection_issue' && (
                      <p className="text-xs text-amber-400 mt-1" data-testid="upload-connection-issue">Connection issues — retrying automatically...</p>
                    )}
                    {videoStep === 'offline' && (
                      <p className="text-xs text-red-400 mt-1" data-testid="upload-offline">You're offline — upload will resume when connection returns</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
                      className="hidden" onChange={handleVideoUpload} />
                    <Button type="button" variant="outline" onClick={() => videoInputRef.current?.click()}
                      className="border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10" data-testid="upload-video-btn">
                      <Upload className="w-4 h-4 mr-2" /> Upload Video
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Thumbnail Upload */}
            <div className="admin-card p-6 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Image className="w-5 h-5 text-[#8B5CF6]" /> Thumbnail *
              </h3>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
                <Info className="w-4 h-4 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-gray-300">
                  <p>Recommended: <strong>1920x1080 px</strong> (16:9 ratio)</p>
                  <p>Min: 1280x720 px &middot; Max size: 20MB &middot; Formats: JPG, PNG, WebP</p>
                </div>
              </div>
              {(form.thumbnailPreview || form.thumbnail) && (
                <div className="relative group">
                  <img src={form.thumbnailPreview || form.thumbnail} alt="Thumbnail" className="w-full max-w-md rounded-lg border border-gray-800"
                    onError={(e) => e.target.style.display = 'none'} />
                  <button onClick={() => { updateForm('thumbnail', ''); updateForm('thumbnailPreview', ''); }}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              )}
              <div className="flex gap-3">
                <input ref={thumbnailInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleThumbnailUpload} />
                <Button type="button" variant="outline" onClick={() => thumbnailInputRef.current?.click()} disabled={uploadingThumb}
                  className="border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/10" data-testid="upload-thumbnail-btn">
                  {uploadingThumb ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {form.thumbnail ? 'Change Thumbnail' : 'Upload Thumbnail'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Genres & Classification */}
        {step === 3 && (
          <div className="space-y-8" data-testid="step-genres">
            {/* Genre Selection (existing) */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Select Genres</h2>
              <p className="text-sm text-gray-400">Choose 1-3 genres that best describe your content.</p>
              <div className="flex flex-wrap gap-3">
                {GENRE_OPTIONS.map((genre) => (
                  <button key={genre} type="button" onClick={() => toggleGenre(genre)}
                    data-testid={`genre-${genre.toLowerCase().replace(/\s/g, '-')}`}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                      form.genres.includes(genre)
                        ? 'bg-[#8B5CF6]/25 text-[#8B5CF6] border-[#8B5CF6]/50 ring-1 ring-[#8B5CF6]/30'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-300'
                    }`}>
                    {genre}
                  </button>
                ))}
              </div>
              <p className="text-sm text-gray-500">{form.genres.length}/3 selected{form.genres.length === 3 ? ' (max)' : ''}</p>
            </div>

            {/* Content Tags – Content Characteristics (new) */}
            <div className="border-t border-gray-800 pt-8 space-y-5" data-testid="content-tags-block">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Content Tags</h2>
                  <p className="text-sm text-gray-400 mt-1">Help viewers understand your content. This section is mandatory for publishing.</p>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0" data-testid="content-rating-badge">
                  <span className="text-xs text-gray-400">Content Rating:</span>
                  <span className={`px-2.5 py-1 text-sm font-bold rounded border tracking-wide ${
                    calcContentRating(form.content_tags) ? 'bg-white/10 border-white/20 text-white' : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                  }`}>
                    {calcContentRating(form.content_tags) || 'Select all tags'}
                  </span>
                </div>
              </div>

              {CONTENT_TAG_CATEGORIES.map((cat) => (
                <div key={cat.key} className="space-y-2" data-testid={`tag-category-${cat.key}`}>
                  <label className="text-sm font-medium text-gray-300">{cat.label}</label>
                  <div className="flex flex-wrap gap-2">
                    {cat.options.map((opt, idx) => (
                      <button key={opt} type="button"
                        data-testid={`tag-${cat.key}-${idx}`}
                        onClick={() => setForm(prev => ({
                          ...prev,
                          content_tags: { ...prev.content_tags, [cat.key]: idx }
                        }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                          form.content_tags[cat.key] === idx
                            ? 'bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/50'
                            : form.content_tags[cat.key] === null
                              ? 'border-yellow-500/30 text-gray-400 hover:border-gray-400 hover:text-gray-300'
                              : 'border-gray-700 text-gray-500 hover:border-gray-500 hover:text-gray-300'
                        }`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                  {cat.helper && (
                    <p className="text-xs text-gray-500 italic">{cat.helper}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Episodes (series only) */}
        {step === 4 && contentType === 'series' && (
          <div className="space-y-6" data-testid="step-episodes">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Upload Episodes</h2>
              <Button onClick={addSeason} variant="outline" size="sm" className="border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/10" data-testid="add-season-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Season
              </Button>
            </div>

            {/* Season Tabs */}
            <div className="flex gap-2 flex-wrap">
              {seasons.map((s, i) => (
                <button key={i} onClick={() => setActiveSeason(i)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeSeason === i
                      ? 'bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/30'
                      : 'bg-[#0F0B1E] text-gray-400 border border-gray-800 hover:border-gray-600'
                  }`} data-testid={`season-tab-${i}`}>
                  Season {s.season_number} ({s.episodes.length} ep)
                  {seasons.length > 1 && (
                    <X className="w-3 h-3 ml-2 inline opacity-50 hover:opacity-100" onClick={(e) => { e.stopPropagation(); removeSeason(i); }} />
                  )}
                </button>
              ))}
            </div>

            {/* Episode List */}
            {seasons[activeSeason]?.episodes.length > 0 && (
              <div className="space-y-2">
                {seasons[activeSeason].episodes.map((ep, i) => (
                  <div key={i} className={`flex items-center gap-4 p-3 rounded-lg border ${editingEpIndex === i ? 'bg-[#8B5CF6]/10 border-[#8B5CF6]/30' : 'bg-[#0F0B1E] border-gray-800'}`}>
                    {ep.thumbnail && <img src={ep.thumbnail} alt="" className="w-20 h-12 object-cover rounded" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">S{ep.season_number}E{ep.episode_number}: {ep.title}</p>
                      <p className="text-xs text-gray-500 truncate">{ep.description || 'No description'}</p>
                    </div>
                    {ep.release_date && (
                      <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                        <Calendar className="w-3 h-3" /> {new Date(ep.release_date + 'T00:00:00').toLocaleDateString()}
                      </span>
                    )}
                    <span className="text-xs text-[#00FF9D]">{ep.video_file ? 'Uploaded' : ''}</span>
                    <button onClick={() => startEditEpisode(i)} className="text-[#8B5CF6] hover:text-[#A78BFA] p-1" data-testid={`edit-ep-${i}`} title="Edit episode">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeEpisode(activeSeason, i)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Episode Form */}
            <div className="admin-card p-5 space-y-4">
              <h3 className="font-semibold text-sm text-[#00FF9D]">
                {editingEpIndex !== null
                  ? `Editing Episode S${seasons[activeSeason]?.season_number}E${editingEpIndex + 1}`
                  : `Add Episode to Season ${seasons[activeSeason]?.season_number}`}
              </h3>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Episode Title *</label>
                <Input value={epForm.title} onChange={(e) => setEpForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Episode title" className="bg-[#030014] border-[#8B5CF6]/20 h-10" data-testid="ep-title" />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <Textarea value={epForm.description} onChange={(e) => setEpForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Episode description" className="bg-[#030014] border-[#8B5CF6]/20 min-h-[60px]" data-testid="ep-description" />
              </div>

              {/* Episode Video Upload */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Episode Video *</label>
                {epForm.video_file ? (
                  <div className="flex items-center gap-3 p-3 bg-[#00FF9D]/5 border border-[#00FF9D]/20 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-[#00FF9D]" />
                    <span className="text-sm text-[#00FF9D] flex-1 truncate">{epForm.videoFileName || 'Uploaded'}</span>
                    <button onClick={() => setEpForm(p => ({ ...p, video_file: '', videoFileName: '' }))}
                      className="p-1 bg-black/40 rounded-full"><X className="w-3 h-3 text-gray-400" /></button>
                  </div>
                ) : uploadingEpVideo ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-4 h-4 text-[#00FF9D] animate-spin" />
                      <span className="text-sm">
                        {epVideoStep && epVideoStep !== 'queued'
                          ? `Optimizing... (${
                              { downloading: 'downloading', analyzing: 'analyzing',
                                transcoding: 'transcoding', faststart: 'optimizing',
                                uploading: 'finalizing', done: 'done' }[epVideoStep] || epVideoStep
                            })`
                          : epVideoProgress >= 96
                            ? 'Optimizing for streaming...'
                            : `Uploading... ${epVideoProgress}%`}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-[#00FF9D] transition-all rounded-full" style={{ width: `${epVideoProgress}%` }} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <input ref={epVideoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska"
                      className="hidden" onChange={handleEpVideoUpload} />
                    <Button type="button" variant="outline" size="sm" onClick={() => epVideoInputRef.current?.click()}
                      className="border-[#00FF9D]/30 text-[#00FF9D] hover:bg-[#00FF9D]/10" data-testid="ep-video-upload-btn">
                      <Upload className="w-3 h-3 mr-1" /> Upload Video
                    </Button>
                  </div>
                )}
              </div>

              {/* Episode thumbnail */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Episode Thumbnail (optional)</label>
                {epForm.thumbnailPreview && <img src={epForm.thumbnailPreview} alt="" className="w-40 h-24 object-cover rounded mb-2 border border-gray-800" />}
                <input ref={epThumbnailInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleEpThumbnailUpload} />
                <Button type="button" variant="outline" size="sm" onClick={() => epThumbnailInputRef.current?.click()} disabled={uploadingEpThumb} className="border-gray-700">
                  {uploadingEpThumb ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                  Upload
                </Button>
              </div>
              {/* Episode release date */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Episode Release Date <span className="text-gray-600 text-xs">(optional)</span>
                </label>
                <Input
                  type="date"
                  value={epForm.release_date}
                  onChange={(e) => setEpForm(p => ({ ...p, release_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-[#030014] border-[#8B5CF6]/20 h-9 max-w-xs text-sm"
                  data-testid="ep-release-date"
                />
                <p className="text-xs text-gray-600 mt-0.5">Schedule this episode independently from the series release date.</p>
              </div>
              {editingEpIndex !== null ? (
                <div className="flex gap-2">
                  <Button onClick={() => { setEditingEpIndex(null); setEpForm({ title: '', description: '', video_file: '', videoFileName: '', thumbnail: '', thumbnailPreview: '', release_date: '' }); }}
                    variant="outline" className="flex-1 border-gray-700 text-gray-400" data-testid="cancel-edit-ep-btn">
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                  <Button onClick={saveEditEpisode} className="flex-1 bg-[#8B5CF6]/20 text-[#8B5CF6] hover:bg-[#8B5CF6]/30" data-testid="save-edit-ep-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </div>
              ) : (
                <Button onClick={addEpisode} className="bg-[#00FF9D]/20 text-[#00FF9D] hover:bg-[#00FF9D]/30 w-full" data-testid="add-episode-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Episode
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Final Step: Review & Submit */}
        {step === totalSteps - 1 && (
          <div className="space-y-6" data-testid="step-review">
            <h2 className="text-xl font-semibold">Review & Submit</h2>
            <div className="admin-card p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div><span className="text-xs text-gray-500">Type</span><p className="font-medium capitalize">{contentType}</p></div>
                  <div><span className="text-xs text-gray-500">Title</span><p className="font-medium">{form.title}</p></div>
                  {form.description && <div><span className="text-xs text-gray-500">Description</span><p className="text-sm text-gray-300">{form.description}</p></div>}
                  <div>
                    <span className="text-xs text-gray-500">Genres</span>
                    <div className="flex gap-2 mt-1">
                      {form.genres.map(g => <span key={g} className="px-2 py-0.5 text-xs rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/30">{g}</span>)}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Content Rating</span>
                    <p className="mt-1"><span className="px-2 py-0.5 text-xs font-bold rounded bg-white/10 border border-white/20">{calcContentRating(form.content_tags) || 'N/A'}</span></p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Video</span>
                    <p className="text-sm text-gray-300">
                      {form.video_file ? `Uploaded: ${form.videoFileName || 'video file'}` : contentType === 'series' ? 'Episode videos uploaded separately' : 'Not uploaded'}
                    </p>
                  </div>
                  {form.release_date && (
                    <div>
                      <span className="text-xs text-gray-500">{contentType === 'series' ? 'Series Release Date' : 'Release Date'}</span>
                      <p className="text-sm text-gray-300 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-[#8B5CF6]" />
                        {new Date(form.release_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  )}
                  {contentType === 'series' && (
                    <div>
                      <span className="text-xs text-gray-500">Episodes</span>
                      <p className="text-sm text-gray-300">{seasons.reduce((a, s) => a + s.episodes.length, 0)} episodes across {seasons.length} season(s)</p>
                    </div>
                  )}
                </div>
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Thumbnail Preview</span>
                  {form.thumbnail ? (
                    <img src={form.thumbnailPreview || form.thumbnail} alt="Thumbnail" className="w-full rounded-lg border border-gray-800" onError={(e) => e.target.style.display = 'none'} />
                  ) : (
                    <div className="w-full aspect-video bg-[#0F0B1E] rounded-lg border border-gray-800 flex items-center justify-center">
                      <p className="text-gray-600 text-sm">No thumbnail</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
              <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-300">Your content will be submitted for owner review. You'll be notified once it's approved.</p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-800">
          <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="border-gray-700 text-gray-400" data-testid="prev-step-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < totalSteps - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="btn-primary" data-testid="next-step-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting || !canProceed()} className="btn-primary px-8" data-testid="submit-content-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><CheckCircle className="w-4 h-4 mr-2" /> Submit for Review</>}
            </Button>
          )}
        </div>
      </div>

      {/* Agreement Popup */}
      <Dialog open={showAgreement} onOpenChange={(open) => { if (!open) handleAgreementDecline(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col bg-[#0F0B1E] border-white/10 p-0" data-testid="upload-agreement-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 shrink-0">
            <DialogTitle className="text-lg font-bold">Creator Content Submission Agreement</DialogTitle>
            <p className="text-sm text-gray-400 mt-1">You must accept this agreement before publishing content.</p>
          </DialogHeader>
          <div
            className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-300 leading-relaxed agreement-content"
            data-testid="upload-agreement-scroll-area"
            dangerouslySetInnerHTML={{ __html: agreementHtml }}
            style={{ maxHeight: '50vh' }}
          />
          <div className="px-6 py-4 border-t border-white/10 flex gap-3 justify-end shrink-0">
            <Button variant="outline" onClick={handleAgreementDecline} className="border-white/10" data-testid="upload-agreement-decline-btn">
              Decline
            </Button>
            <Button onClick={handleAgreementAccept} className="btn-primary" data-testid="upload-agreement-accept-btn">
              I Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadContentPage;
